// src/providers/NotificationsProvider.tsx
import { useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { isNotificationsSupported } from "../services/localNotifications";
import {
  bootstrapNotificationPreferences,
  getNotificationPreferences,
  subscribeNotificationPreferences,
  type NotificationPreferences,
} from "../services/notificationPreferences";
import {
  getMyActiveClubIds,
  getMyActiveTeamIds,
  subscribeClubPostsNotifications,
  subscribeTeamChatNotifications,
  syncUpcomingEventReminders,
} from "../services/notificationSync";
import { registerForPush, resyncPushToken } from "../services/pushNotifications";

const RESYNC_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6h, tant que l'app reste ouverte

export default function NotificationsProvider() {
  const cleanupRealtimeRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!isNotificationsSupported()) return;

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    async function runSync(prefs: NotificationPreferences) {
      cleanupRealtimeRef.current();
      cleanupRealtimeRef.current = () => {};
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      if (!prefs.enabled) return;

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      // Rattrape un token push reçu avant que la session ne soit prête
      // (cas fréquent au lancement à froid), puis (re)tente un enregistrement
      // normal — idempotent côté Capacitor/serveur (upsert sur le token).
      void resyncPushToken();
      void registerForPush();

      const [teamIds, clubIds] = await Promise.all([
        getMyActiveTeamIds(user.id),
        getMyActiveClubIds(user.id),
      ]);
      if (cancelled) return;

      const cleanups: Array<() => void> = [];

      if (prefs.categories.matches) {
        void syncUpcomingEventReminders(teamIds, prefs.reminderMinutes);
        intervalId = setInterval(() => {
          void syncUpcomingEventReminders(teamIds, prefs.reminderMinutes);
        }, RESYNC_INTERVAL_MS);
      }

      if (prefs.categories.chat) {
        cleanups.push(subscribeTeamChatNotifications(teamIds, user.id));
      }

      if (prefs.categories.posts) {
        cleanups.push(subscribeClubPostsNotifications(teamIds, clubIds, user.id));
      }

      cleanupRealtimeRef.current = () => cleanups.forEach((fn) => fn());
    }

    bootstrapNotificationPreferences().then((prefs) => {
      if (!cancelled) void runSync(prefs);
    });

    const unsubscribePrefs = subscribeNotificationPreferences((prefs) => {
      void runSync(prefs);
    });

    const { data: authSub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") {
        bootstrapNotificationPreferences().then((prefs) => {
          if (!cancelled) void runSync(prefs);
        });
      } else if (event === "SIGNED_OUT") {
        getNotificationPreferences().then((prefs) => {
          if (!cancelled) void runSync(prefs);
        });
      }
    });

    return () => {
      cancelled = true;
      unsubscribePrefs();
      authSub.subscription.unsubscribe();
      cleanupRealtimeRef.current();
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  return null;
}
